import { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, AlertTitle, Box, Button, Stack, Typography } from '@mui/material';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Route-level boundary. Without one, a render-time throw in any page unmounts
 * the whole tree and leaves an empty document with no indication of what broke
 * — the failure mode that hid the gear catalog defect (refs #1656, F4).
 *
 * Keeps the message generic on purpose: the boundary is user-facing, so the
 * underlying error goes to the console for engineers rather than onto the page.
 */
class RouteErrorBoundary extends Component<Props, State> {
  public state: State = { error: null };

  public static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  public componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('Route render failed', error, info.componentStack);
  }

  private readonly handleReload = () => {
    this.setState({ error: null });
    window.location.reload();
  };

  public render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <Alert severity="error" sx={{ maxWidth: 640, width: '100%' }}>
          <AlertTitle>This page could not be displayed</AlertTitle>
          <Stack spacing={2} alignItems="flex-start">
            <Typography variant="body2">
              Something went wrong while rendering this page. Your work has not
              been changed. Reload to try again, and report the problem if it
              keeps happening.
            </Typography>
            <Button variant="outlined" size="small" onClick={this.handleReload}>
              Reload page
            </Button>
          </Stack>
        </Alert>
      </Box>
    );
  }
}

export default RouteErrorBoundary;
